<template>
  <Card>
    <template #title> {{ $t('forms.device.import.device_import_form.import_devices') }}</template>
    <template #subtitle> {{ $t('forms.device.import.device_import_form.import_tool_will_create_devices') }}</template>
    <template #content>
      <div class="grid grid-cols-6 gap-8">
        <FormGroup class="col-span-3" :error-text="form.errors?.company_id" :label="$t('forms.device.device_properties_form.company')">
          <Dropdown
              v-model="form.company_id"
              :disabled="disabled || form.processing"
              :options="companiesStore.getCompanyOptions"
              filter
              option-label="name"
              option-value="id"
              show-clear
              @change="form.validate('company_id')"
          />
        </FormGroup>

        <FormGroup class="col-span-3" :error-text="form.errors?.vehicle_manufacturer_id"
                   :label="$t('forms.device.device_properties_form.vehicle_manufacturer')">
          <Dropdown
              v-model="form.vehicle_manufacturer_id"
              :disabled="disabled || form.processing"
              :options="vehicleManufacturers"
              :placeholder="$t('common.na')"
              option-label="name"
              option-value="id"
              show-clear
              @change="form.validate('vehicle_manufacturer_id')"
          />
        </FormGroup>

        <FormGroup class="col-span-2" :error-text="form.errors?.device_type_id"
                   :label="$t('forms.device.device_properties_form.device_type')">
          <Dropdown
              v-model="form.device_type_id"
              :disabled="disabled || form.processing"
              :options="deviceTypesStore.getDeviceTypeOptions"
              option-label="name"
              option-value="id"
              @change="onDeviceTypeChange()"
          />
        </FormGroup>

        <FormGroup class="col-span-2" :error-text="form.errors.device_model_id"
                   :label="$t('forms.device.device_properties_form.device_model')">
          <Dropdown
              v-model="form.device_model_id"
              :disabled="disabled || form.processing"
              :options="deviceModels"
              option-label="name"
              option-value="id"
              @change="onDeviceModelChange()"
          />
        </FormGroup>

        <FormGroup class="col-span-2" :error-text="form.errors?.device_revision_id"
                   :label="$t('forms.device.device_properties_form.device_revision')">
          <Dropdown
              v-model="form.device_revision_id"
              :disabled="disabled || form.processing"
              :options="deviceRevisions"
              option-label="name"
              option-value="id"
              @change="form.validate('device_revision_id')"
          />
        </FormGroup>

        <label
            class="col-span-3 hover:bg-slate-50 cursor-pointer transition-colors duration-200 p-4 rounded-md flex flex-col items-start justify-between ring-1 ring-surface-300">
          <FormGroup
              :error-text="form.errors?.create_assets"
              :help-text="$t('forms.device.import.device_import_form.each_new_device_will_create_a_placeholder_asset')"
              :label="$t('forms.device.import.device_import_form.create_assets')"
              class="mb-2"
          />
          <InputSwitch
              v-model="form.create_assets"
              :disabled="form.processing"
              :false-value="0"
              :true-value="1"
              @change="form.validate('create_assets')"
          />
        </label>

        <FormGroup class="col-span-full" :error-text="form.errors?.import_file" :label="$t('forms.device.import.device_import_form.file_import')">
          <FileUpload
              :disabled="form.processing"
              :file-limit="1"
              :preview-width="0"
              :show-upload-button="false"
              accept=".xlsx,.csv,.xls"
              :cancel-label="$t('forms.device.import.device_import_form.clear')"
              custom-upload
              name="demo[]"
              :upload-label="$t('forms.device.import.device_import_form.start_import')"
              :choose-label="$t('forms.device.import.device_import_form.choose_file')"
              @clear="removeFile()"
              @select="useFile($event)"
              @before-upload="event => console.log(event)"
          >
            <template #empty>
              <p>{{ $t('forms.device.import.device_import_form.drag_and_drop_files') }}</p>
            </template>

            <template #content="{ files, removeFileCallback }">
              <div v-if="files.length > 0">
                <ProgressSpinner v-if="loadingPreview" class="h-4 w-4"/>

                <template v-if="headings">
                <div v-for="(file, index) of files" :key="file.name + file.type + file.size"
                     class="card m-0 flex flex-col border-1 surface-border align-items-center gap-3">
                  <span class="text-base font-semibold">{{ file.name }}</span>
                  <div class="text-sm text-slate-500"><span class="font-semibold">{{ $t('forms.device.import.device_import_form.row_count', { count: $n(headings.row_count) }) }}</span> ({{ useFilesize(file.size) }})
                  </div>

                  <p>{{ $t('forms.device.import.device_import_form.column_mapping_instructions') }}</p>

                  <div class="w-full overflow-x-scroll">
                  <table class="text-sm text-left border-collapse">
                    <thead>
                    <tr>
                      <th v-for="(value, name, index) in form.sheet_headers"
                          class="py-2 px-3 border border-slate-300 border-b-0 truncate whitespace-nowrap bg-slate-100">
                        <span>{{ name }}</span>
                      </th>
                    </tr>
                    <tr>
                      <th v-for="(value, name, index) in form.sheet_headers" class="py-1 px-1 border border-slate-300 border-t-0 bg-slate-100">
                        <div class="flex flex-col min-w-[8rem]">
                          <Dropdown v-model="form.sheet_headers[name]" :options="headings.sheet_headers" show-clear/>
                        </div>
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in headings.row_preview">
                      <template v-for="(value, name, index) in form.sheet_headers">
                        <td v-if="row[value]" class="py-2 px-3 border border-slate-300">
                          {{ row[value] }}
                        </td>
                        <td v-else-if="form[name]" class="py-2 px-3 border border-slate-300 bg-slate-100 opacity-50">
                          {{ form[name] }}
                        </td>
                        <td v-else class="py-2 px-3 border border-slate-300">
                          {{ $t('forms.device.import.device_import_form.empty_value') }}
                        </td>
                      </template>
                    </tr>

                    <tr>
                      <td v-for="(value, name, index) in form.sheet_headers" class="py-2 px-3 border-b-slate-100 border border-slate-300 bg-gradient-to-t from-slate-100 opacity-50">...</td>
                    </tr>
                    </tbody>
                  </table>
                  </div>
                  <div>
                    <Button :label="$t('forms.device.import.device_import_form.remove_file')" outlined severity="danger" @click="removeFileCallback(index)"/>
                  </div>
                </div>
                </template>
              </div>
            </template>
          </FileUpload>
        </FormGroup>

      </div>
    </template>
    <template #footer>
      <div class="flex items-center justify-end">
        <Button :disabled="!requiredFields() || form.processing"
                :label="$t('forms.device.import.device_import_form.import_file')"
                size="large" @click="sendImportRequest()"/>
      </div>
    </template>
  </Card>
</template>

<script>

import {IconDots, IconFileCertificate, IconFileCode, IconFileTypePdf} from '@tabler/icons-vue';
import FileInput from "@/Components/FileInput.vue";
import FormGroup from "@/Components/FormGroup.vue";
import FileTypeIcon from "@/Components/FileTypeIcon.vue";
import SelectAssetDropdown from "@/Components/SelectAssetDropdown.vue";
import {mapStores} from "pinia";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useDeviceTypesStore} from "@/stores/deviceTypes.js";
import {useDeviceModelsStore} from "@/stores/deviceModels.js";
import {useCompaniesStore} from "@/stores/companies.js";
import {useCreateForm} from "@/stores/_helpers.js";
import {useFilesize} from "@/Composables/Filesize.js";

export default {
  name: "DeviceImportForm",
  components: {
    SelectAssetDropdown,
    FileTypeIcon,
    FormGroup,
    FileInput,
    IconDots,
    IconFileCode,
    IconFileTypePdf,
    IconFileCertificate,
  },
  emits: [
    'on-save',
  ],
  props: {
    form: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      headings: undefined,
      loadingPreview: false,
    }
  },
  computed: {
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useDeviceTypesStore),
    ...mapStores(useDeviceModelsStore),
    ...mapStores(useCompaniesStore),
    deviceModels() {
      return this.deviceModelsStore.getDeviceModelOptions?.filter(deviceModel => {
        return deviceModel.device_type_id === this.form.device_type_id;
      }) || [];
    },
    deviceRevisions() {
      return this.deviceRevisionsStore.getDeviceRevisionOptions?.filter(deviceRevision => {
        return deviceRevision.device_model_id === this.form.device_model_id;
      }) || [];
    },
    vehicleManufacturers() {
      return this.companiesStore.getCompanyOptions?.filter(company => {
        return !!company.vehicle_manufacturer;
      }) || [];
    },
  },
  methods: {
    useFilesize,
    toggle(e) {
      this.$refs.menu.toggle(e);
    },
    async useFile(input) {
      try {
        this.loadingPreview = true;

        // await pluck sheet headers...
        const headingsForm = useCreateForm('device-imports/headings', {
          import_file: input.files[0],
          import_type_id: this.form.import_type_id,
        });

          this.headings = (await headingsForm.submit()).data

          // Try to match values returned from the spreadsheet to columns automatically
          Object.keys(this.form.sheet_headers).forEach(header => {
            if(this.form.sheet_headers[header] === null){
              this.form.sheet_headers[header] = this.headings.sheet_headers.find(value => value === header)
            }
          })

          this.form.import_file = input.files[0];
      } catch (e) {
        this.headings = undefined;
        console.error(e);
      } finally {
        this.loadingPreview = false;
      }
    },
    removeFile() {
      this.form.import_file = undefined;
      this.headings = undefined;
      this.form.reset('sheet_headers');
      console.log(this.form);
    },
    async sendImportRequest() {
      this.form.processing = true;
      try {
        await this.form.submit();
        this.$toast.add({
          severity: 'success',
          summary: this.$t('forms.device.import.device_import_form.importing'),
          detail: this.$t('forms.device.import.device_import_form.import_in_progress'),
          life: 5000
        });
        this.$emit('on-save');
      } catch (error) {
        console.log(error);
      } finally {
        this.form.processing = false;
      }
    },
    onDeviceTypeChange() {
      this.form.device_model_id = null;
      this.onDeviceModelChange();
    },
    onDeviceModelChange() {
      this.form.device_revision_id = null;
      this.form.validate();
    },
    requiredFields() {
      return true;
    }
  },
  mounted() {
    this.deviceRevisionsStore.indexDeviceRevisionOptions();
    this.deviceTypesStore.indexDeviceTypeOptions();
    this.deviceModelsStore.indexDeviceModelOptions();
    this.companiesStore.indexCompanyOptions();
  },
}
</script>

<style scoped>

</style>
